"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { playSound } from "@/app/utils";

export default function CreateGamePage() {
  const [gameName, setGameName] = useState("");
  const [startingGold, setStartingGold] = useState(380);
  const [maxCharacters, setMaxCharacters] = useState(100);
  const [maxUnitsPerTurn, setMaxUnitsPerTurn] = useState(100);
  const [useFortress, setUseFortress] = useState(false);
  const [useSpells, setUseSpells] = useState(false);
  const [useUpgrades, setUseUpgrades] = useState(false);
  const [useMounts, setUseMounts] = useState(true);
  const [useTreasureChests, setUseTreasureChests] = useState(true);
  const [usePickups, setUsePickups] = useState(true);
  const [buildStage, setBuildStage] = useState(true);
  const router = useRouter();

  function handleCreateGame(e) {
    e.preventDefault();

    if (!gameName.trim()) {
      alert("Game name cannot be empty");
      return;
    }

    // Create a new game
    fetch("/api/games/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        gameName,
        settings: {
          startingGold,
          maxCharacters,
          maxUnitsPerTurn,
          useFortress,
          useSpells,
          useUpgrades,
          useMounts,
          useTreasureChests,
          usePickups,
          buildStage,
        },
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          alert(`Error creating game: ${data.error}`);
          return;
        }
        router.push(`/game/${data.game.id}`);
      })
      .catch((error) => {
        console.error("Error creating game:", error);
        alert("Failed to create game. Please try again.");
      });
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-2">
      <form onSubmit={handleCreateGame}>
        <div className="w-96 space-y-8">
          <div>
            <label className="block text-gray-300 mb-2 text-lg font-semibold">
              Game Name
            </label>
            <input
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              placeholder="Enter name..."
              autoFocus
              className="shadow appearance-none border rounded w-full block py-3 px-4 text-gray-700 leading-tight focus:ring-4 focus:ring-purple-600 focus:outline-none focus:shadow-outline"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              Starting Gold
            </label>
            <input
              type="number"
              value={startingGold || ""}
              onChange={(e) => setStartingGold(parseInt(e.target.value))}
              className="shadow appearance-none border rounded block py-3 px-4 text-gray-700 leading-tight focus:ring-4 focus:ring-purple-600 focus:outline-none focus:shadow-outline"
            />
            <p className="text-sm text-gray-500 mt-2">
              Amount of gold each player starts with
            </p>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              Max Characters Per Player
            </label>
            <input
              type="number"
              value={maxCharacters || ""}
              onChange={(e) => setMaxCharacters(parseInt(e.target.value))}
              min="1"
              className="shadow appearance-none border rounded block py-3 px-4 text-gray-700 leading-tight focus:ring-4 focus:ring-purple-600 focus:outline-none focus:shadow-outline"
            />
            <p className="text-sm text-gray-600 mt-2">
              Maximum total characters each player can build
            </p>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              Max Units Built Per Turn
            </label>
            <input
              type="number"
              value={maxUnitsPerTurn || ""}
              onChange={(e) => setMaxUnitsPerTurn(parseInt(e.target.value))}
              min="1"
              className="shadow appearance-none border rounded block py-3 px-4 text-gray-700 leading-tight focus:ring-4 focus:ring-purple-600 focus:outline-none focus:shadow-outline"
            />
            <p className="text-sm text-gray-600 mt-2">
              Maximum number of units a player can build each turn (default 100)
            </p>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={useFortress}
                onChange={(e) => setUseFortress(e.target.checked)}
                className="mr-2"
              />
              Fortresses
            </label>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={useSpells}
                onChange={(e) => setUseSpells(e.target.checked)}
                className="mr-2"
              />
              Spells
            </label>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={useUpgrades}
                onChange={(e) => setUseUpgrades(e.target.checked)}
                className="mr-2"
              />
              Upgrades
            </label>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={useMounts}
                onChange={(e) => setUseMounts(e.target.checked)}
                className="mr-2"
              />
              Mounts
            </label>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={useTreasureChests}
                onChange={(e) => setUseTreasureChests(e.target.checked)}
                className="mr-2"
              />
              Treasure Chests
            </label>
            <p className="text-sm text-gray-600 mt-2">
              Enable treasure chests that can be collected for gold
            </p>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={usePickups}
                onChange={(e) => setUsePickups(e.target.checked)}
                className="mr-2"
              />
              Pick Ups
            </label>
            <p className="text-sm text-gray-600 mt-2">
              Allow characters to pick up items during the game
            </p>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 font-semibold">
              <input
                type="checkbox"
                checked={buildStage}
                onChange={(e) => setBuildStage(e.target.checked)}
                className="mr-2"
              />
              Build Stage
            </label>
            <p className="text-sm text-gray-600 mt-2">
              Players start in Build stage where they can build and upgrade
              units before battle begins
            </p>
          </div>

          <button
            type="submit"
            className="bg-blue-500 font-bold hover:bg-blue-700 text-white py-3 px-4 rounded focus:ring-4 focus:ring-blue-800 focus:outline-none focus:shadow-outline mt-6 w-full"
          >
            Create Game
          </button>
        </div>
      </form>
    </div>
  );
}
